import {
  collection,
  getDocs,
} from 'firebase/firestore';

import { db } from '../../firebase';
import { CURATED_EQUIPMENT_LIBRARY_BUNDLE } from '../../data/equipmentLibrary';
import {
  applyCompanyEquipmentPatch,
  type CompanyEquipmentProductView,
} from '../../lib/equipmentLibrary/companyPatches';
import {
  validateCompanyEquipmentPatch,
  validateEquipmentAdmission,
  validateEquipmentMarketEvidence,
  validateEquipmentObservation,
  validateEquipmentProduct,
  validateEquipmentRevision,
  validateEquipmentSource,
  type EquipmentLibraryValidationResult,
} from '../../lib/equipmentLibrary/validators';
import type {
  CompanyEquipmentPatch,
  EquipmentAdmission,
  EquipmentLibraryProduct,
  EquipmentMarketEvidence,
  EquipmentObservation,
  EquipmentRevision,
  EquipmentSource,
} from '../../types/equipmentLibrary';

export const EQUIPMENT_LIBRARY_COLLECTIONS = Object.freeze({
  products: 'equipment_products',
  revisions: 'equipment_product_revisions',
  sources: 'equipment_sources',
  observations: 'equipment_observations',
  admissions: 'equipment_admissions',
  marketEvidence: 'equipment_market_evidence',
  planningBenchmarks: 'planning_benchmarks',
  legacyIds: 'equipment_legacy_ids',
  privateProducts: 'private_equipment_products',
  companyPatches: 'equipment_product_patches',
});

export type EquipmentAdmissionLevel =
  | 'library-visible'
  | 'bom-ready'
  | 'screening-ready'
  | 'simulation-ready';

export interface EquipmentAdmissionSpine {
  'library-visible': EquipmentAdmission['status'] | 'not-admitted';
  'bom-ready': EquipmentAdmission['status'] | 'not-admitted';
  'screening-ready': EquipmentAdmission['status'] | 'not-admitted';
  'simulation-ready': EquipmentAdmission['status'] | 'not-admitted';
}

export interface EquipmentLibraryData {
  products: readonly EquipmentLibraryProduct[];
  revisions: readonly EquipmentRevision[];
  sources: readonly EquipmentSource[];
  observations: readonly EquipmentObservation[];
  admissions: readonly EquipmentAdmission[];
  marketEvidence: readonly EquipmentMarketEvidence[];
  companyPatches: readonly CompanyEquipmentPatch[];
}

export interface EquipmentLibraryRow {
  product: EquipmentLibraryProduct;
  revision?: EquipmentRevision;
  view: CompanyEquipmentProductView;
  sources: readonly EquipmentSource[];
  observations: readonly EquipmentObservation[];
  admissions: readonly EquipmentAdmission[];
  marketEvidence: readonly EquipmentMarketEvidence[];
  admissionSpine: EquipmentAdmissionSpine;
  evidenceTier: string;
  researchOnly: boolean;
  sourceKind: 'oem' | 'registry' | 'private';
}

export interface EquipmentLibraryIndex {
  compiledAt: string;
  rows: readonly EquipmentLibraryRow[];
  warnings: readonly string[];
}

export interface EquipmentLibraryQuery {
  search?: string;
  manufacturer?: string;
  kind?: string;
  technology?: string;
  fuel?: string;
  primeMover?: string;
  cycle?: string;
  frequencyHz?: 50 | 60;
  region?: string;
  application?: string;
  lifecycleStatus?: string;
  evidenceTier?: string;
  admissionLevel?: EquipmentAdmissionLevel;
  includeResearchOnly?: boolean;
  includeInactive?: boolean;
  pageSize?: number;
  cursor?: string;
}

export interface EquipmentLibraryPage {
  rows: readonly EquipmentLibraryRow[];
  total: number;
  nextCursor: string | null;
  warnings: readonly string[];
}

const SURFACE_BY_LEVEL: Record<
  EquipmentAdmissionLevel,
  EquipmentAdmission['surface']
> = {
  'library-visible': 'browse',
  'bom-ready': 'equipment-bom',
  'screening-ready': 'generation-screening',
  'simulation-ready': 'thermodynamic-solver',
};

function byId<T extends { id: string }>(rows: readonly T[]): Map<string, T> {
  return new Map(rows.map(row => [row.id, row]));
}

function mergeById<T extends { id: string }>(
  baseline: readonly T[],
  overlay: readonly T[],
): T[] {
  const merged = byId(baseline);
  overlay.forEach(row => merged.set(row.id, row));
  return [...merged.values()];
}

function admissionSpine(
  admissions: readonly EquipmentAdmission[],
): EquipmentAdmissionSpine {
  const status = (level: EquipmentAdmissionLevel): EquipmentAdmission['status'] | 'not-admitted' => {
    const surface = SURFACE_BY_LEVEL[level];
    const candidates = admissions
      .filter(admission => admission.surface === surface)
      .sort((left, right) => left.reviewedAt.localeCompare(right.reviewedAt));
    return candidates.at(-1)?.status ?? 'not-admitted';
  };
  return {
    'library-visible': status('library-visible'),
    'bom-ready': status('bom-ready'),
    'screening-ready': status('screening-ready'),
    'simulation-ready': status('simulation-ready'),
  };
}

function tierFor(product: EquipmentLibraryProduct): string {
  return product.tags.find(tag => tag.startsWith('tier-'))
    ?? (product.visibility === 'company-private' ? 'private-unverified' : 'evidence-unclassified');
}

function buildPatchedView(
  product: EquipmentLibraryProduct,
  revision: EquipmentRevision | undefined,
  companyId: string | null | undefined,
  patches: readonly CompanyEquipmentPatch[],
  warnings: string[],
): CompanyEquipmentProductView {
  const effectiveCompanyId = companyId
    ?? (product.visibility === 'company-private' ? product.companyId : '');
  let view: CompanyEquipmentProductView = {
    canonicalProduct: product,
    ...(revision ? { canonicalRevision: revision } : {}),
    displayName: product.displayName,
    tags: [...product.tags],
    preferredSupplierIds: [],
    privateObservationIds: [],
    privateMarketEvidenceIds: [],
    active: true,
    appliedPatchIds: [],
  };
  if (!effectiveCompanyId) return view;

  const applicable = patches
    .filter(patch => (
      patch.companyId === effectiveCompanyId
      && patch.targetProductId === product.id
    ))
    .sort((left, right) => (
      left.updatedAt.localeCompare(right.updatedAt)
      || left.id.localeCompare(right.id)
    ));
  for (const patch of applicable) {
    try {
      view = applyCompanyEquipmentPatch(view, patch, effectiveCompanyId);
    } catch (error) {
      warnings.push(`Patch ${patch.id} ignored: ${(error as Error).message}`);
    }
  }
  return view;
}

/**
 * Build the complete permitted index. Canonical records stay immutable;
 * tenant patches affect only the explicit presentation/commercial view.
 */
export function buildEquipmentLibraryIndex(
  data: EquipmentLibraryData,
  options: {
    companyId?: string | null;
    compiledAt?: string;
    warnings?: readonly string[];
  } = {},
): EquipmentLibraryIndex {
  const revisionMap = byId(data.revisions);
  const sourceMap = byId(data.sources);
  const warnings = [...(options.warnings ?? [])];

  const rows = data.products
    .filter(product => (
      product.visibility !== 'company-private'
      || product.companyId === options.companyId
    ))
    .map((product): EquipmentLibraryRow => {
      const revision = product.currentRevisionId
        ? revisionMap.get(product.currentRevisionId)
        : undefined;
      if (product.currentRevisionId && !revision) {
        warnings.push(
          `Product ${product.id} references missing revision ${product.currentRevisionId}.`,
        );
      }
      const observations = revision
        ? data.observations.filter(observation => (
          observation.revisionId === revision.id
          && revision.observationIds.includes(observation.id)
        ))
        : [];
      const admissions = revision
        ? data.admissions.filter(admission => admission.revisionId === revision.id)
        : [];
      const marketEvidence = data.marketEvidence.filter(evidence => (
        evidence.productId === product.id
        && (evidence.revisionId == null || evidence.revisionId === revision?.id)
      ));
      const sources = revision
        ? revision.sourceIds
          .map(sourceId => sourceMap.get(sourceId))
          .filter((source): source is EquipmentSource => source != null)
        : [];
      const view = buildPatchedView(
        product,
        revision,
        options.companyId,
        data.companyPatches,
        warnings,
      );
      const spine = admissionSpine(admissions);
      return {
        product,
        ...(revision ? { revision } : {}),
        view,
        sources,
        observations,
        admissions,
        marketEvidence,
        admissionSpine: spine,
        evidenceTier: tierFor(product),
        researchOnly: spine['library-visible'] === 'comparison-only'
          || product.lifecycleStatus !== 'commercial-current'
          || product.visibility === 'company-private',
        sourceKind: product.visibility === 'company-private'
          ? 'private'
          : product.visibility === 'public-registry'
            ? 'registry'
            : 'oem',
      };
    })
    .sort((left, right) => (
      left.product.manufacturer.localeCompare(
        right.product.manufacturer,
        undefined,
        { sensitivity: 'base' },
      )
      || left.view.displayName.localeCompare(
        right.view.displayName,
        undefined,
        { sensitivity: 'base' },
      )
      || left.product.id.localeCompare(right.product.id)
    ));

  return {
    compiledAt: options.compiledAt ?? CURATED_EQUIPMENT_LIBRARY_BUNDLE.compiledAt,
    rows,
    warnings,
  };
}

function includesFolded(values: readonly string[], target: string): boolean {
  const folded = target.trim().toLocaleLowerCase();
  return values.some(value => value.toLocaleLowerCase() === folded);
}

function matchesAdmission(
  row: EquipmentLibraryRow,
  level: EquipmentAdmissionLevel,
): boolean {
  return row.admissionSpine[level] === 'admitted';
}

export function queryEquipmentLibraryIndex(
  index: EquipmentLibraryIndex,
  query: EquipmentLibraryQuery = {},
): EquipmentLibraryPage {
  const search = query.search?.trim().toLocaleLowerCase() ?? '';
  const filtered = index.rows.filter(row => {
    const { product, revision, view } = row;
    if (!query.includeInactive && !view.active) return false;
    if (!query.includeResearchOnly && row.researchOnly) return false;
    if (query.manufacturer && product.manufacturer !== query.manufacturer) return false;
    if (query.kind && product.kind !== query.kind) return false;
    if (query.technology && !includesFolded(product.facets.technologies, query.technology)) return false;
    if (query.fuel && !includesFolded(product.facets.fuels, query.fuel)) return false;
    if (query.primeMover && !includesFolded(product.facets.primeMovers, query.primeMover)) return false;
    if (query.cycle && !includesFolded(product.facets.cycles, query.cycle)) return false;
    if (query.frequencyHz && !revision?.frequencyHz.includes(query.frequencyHz)) return false;
    if (query.region && !includesFolded(product.facets.regions, query.region)) return false;
    if (query.application && !includesFolded(product.facets.applications, query.application)) return false;
    if (query.lifecycleStatus && product.lifecycleStatus !== query.lifecycleStatus) return false;
    if (query.evidenceTier && row.evidenceTier !== query.evidenceTier) return false;
    if (query.admissionLevel && !matchesAdmission(row, query.admissionLevel)) return false;
    if (search) {
      const haystack = [
        product.manufacturer,
        product.family,
        product.model,
        product.displayName,
        view.displayName,
        product.kind,
        product.domain,
        product.lifecycleStatus,
        revision?.variantLabel,
        ...view.tags,
        ...product.facets.technologies,
        ...product.facets.energySources,
        ...product.facets.fuels,
        ...product.facets.primeMovers,
        ...product.facets.cycles,
        ...product.facets.applications,
        ...product.facets.regions,
        ...row.sources.flatMap(source => [source.publisher, source.title]),
      ].filter((value): value is string => Boolean(value))
        .join(' ')
        .toLocaleLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });

  const pageSize = Math.min(100, Math.max(1, query.pageSize ?? 25));
  const cursorIndex = query.cursor
    ? filtered.findIndex(row => row.product.id === query.cursor)
    : -1;
  const start = cursorIndex >= 0 ? cursorIndex + 1 : 0;
  const rows = filtered.slice(start, start + pageSize);
  const hasMore = start + rows.length < filtered.length;
  return {
    rows,
    total: filtered.length,
    nextCursor: hasMore ? rows.at(-1)?.product.id ?? null : null,
    warnings: index.warnings,
  };
}

type Validator = (value: unknown) => EquipmentLibraryValidationResult;

async function fetchValidatedCollection<T extends { id: string }>(
  path: string,
  validator: Validator,
  warnings: string[],
): Promise<T[]> {
  try {
    const snapshot = await getDocs(collection(db, path));
    const rows: T[] = [];
    snapshot.forEach(document => {
      const value = document.data();
      const validation = validator(value);
      if (!validation.valid || value.id !== document.id) {
        warnings.push(
          `${path}/${document.id} ignored: ${validation.issues
            .map(issue => `${issue.path} ${issue.message}`)
            .join('; ') || 'document id does not match payload id'}`,
        );
        return;
      }
      rows.push(value as T);
    });
    return rows;
  } catch (error) {
    warnings.push(`${path} unavailable: ${(error as Error).message}`);
    return [];
  }
}

/**
 * Dual-read loader: the immutable shipped snapshot guarantees offline breadth;
 * valid Firestore records overlay by stable id and tenant-private data is read
 * only from the selected company path.
 */
export async function loadEquipmentLibraryIndex(
  companyId?: string | null,
): Promise<EquipmentLibraryIndex> {
  const warnings: string[] = [];
  const [
    products,
    revisions,
    sources,
    observations,
    admissions,
    marketEvidence,
    privateProducts,
    companyPatches,
  ] = await Promise.all([
    fetchValidatedCollection<EquipmentLibraryProduct>(
      EQUIPMENT_LIBRARY_COLLECTIONS.products,
      validateEquipmentProduct,
      warnings,
    ),
    fetchValidatedCollection<EquipmentRevision>(
      EQUIPMENT_LIBRARY_COLLECTIONS.revisions,
      validateEquipmentRevision,
      warnings,
    ),
    fetchValidatedCollection<EquipmentSource>(
      EQUIPMENT_LIBRARY_COLLECTIONS.sources,
      validateEquipmentSource,
      warnings,
    ),
    fetchValidatedCollection<EquipmentObservation>(
      EQUIPMENT_LIBRARY_COLLECTIONS.observations,
      validateEquipmentObservation,
      warnings,
    ),
    fetchValidatedCollection<EquipmentAdmission>(
      EQUIPMENT_LIBRARY_COLLECTIONS.admissions,
      validateEquipmentAdmission,
      warnings,
    ),
    fetchValidatedCollection<EquipmentMarketEvidence>(
      EQUIPMENT_LIBRARY_COLLECTIONS.marketEvidence,
      validateEquipmentMarketEvidence,
      warnings,
    ),
    companyId
      ? fetchValidatedCollection<EquipmentLibraryProduct>(
        `companies/${companyId}/${EQUIPMENT_LIBRARY_COLLECTIONS.privateProducts}`,
        validateEquipmentProduct,
        warnings,
      )
      : Promise.resolve([]),
    companyId
      ? fetchValidatedCollection<CompanyEquipmentPatch>(
        `companies/${companyId}/${EQUIPMENT_LIBRARY_COLLECTIONS.companyPatches}`,
        validateCompanyEquipmentPatch,
        warnings,
      )
      : Promise.resolve([]),
  ]);

  return buildEquipmentLibraryIndex({
    products: mergeById(
      CURATED_EQUIPMENT_LIBRARY_BUNDLE.products,
      [...products, ...privateProducts],
    ),
    revisions: mergeById(CURATED_EQUIPMENT_LIBRARY_BUNDLE.revisions, revisions),
    sources: mergeById(CURATED_EQUIPMENT_LIBRARY_BUNDLE.sources, sources),
    observations: mergeById(
      CURATED_EQUIPMENT_LIBRARY_BUNDLE.observations,
      observations,
    ),
    admissions: mergeById(
      CURATED_EQUIPMENT_LIBRARY_BUNDLE.admissions,
      admissions,
    ),
    marketEvidence: mergeById(
      CURATED_EQUIPMENT_LIBRARY_BUNDLE.marketEvidence,
      marketEvidence,
    ),
    companyPatches,
  }, {
    companyId,
    warnings,
  });
}

